import {
  useTrustpilotReviews,
  type TrustpilotDisplayData,
} from "@made-people/grease-trustpilot";
import { Swiper } from "@made-people/blueprint-swiper";
import { useTranslate } from "@made-people/grease-translations";
import { openReviewsModal } from "./trustpilot-drawer";
import { StarRating, ReviewCard } from "./trustpilot-shared";
import { useLocale } from "../../../../hooks/use-locale";
import { TRUSTPILOT_CONSTANTS } from "../trustpilot-constants";
import { withManagedProps } from "@made-people/managed-props";
import { z } from "zod";
import { withZod } from "@made-people/react-prop-guard";

export const trustpilotReviewsSchema = z.object({
  type: z.enum(["product", "store"]).default("store"),
  productSku: z.string().optional(),
  reviewsData: z.custom<TrustpilotDisplayData | null>().optional(),
});

export type TrustpilotReviewsProps = z.infer<typeof trustpilotReviewsSchema>;

export const TrustpilotReviews = withManagedProps(
  "TrustpilotReviews",
  withZod(
    trustpilotReviewsSchema,
    ({
      type,
      productSku,
      reviewsData: productReviewsData,
    }: TrustpilotReviewsProps) => {
      const storeReviewsData = useTrustpilotReviews(type);
      const translate = useTranslate();
      const locale = useLocale();

      const data = type === "product" ? productReviewsData : storeReviewsData;

      if (!data) {
        return null;
      }

      const { averageRating, totalReviews } = data;
      const reviews = data.reviews.slice(
        0,
        TRUSTPILOT_CONSTANTS.REVIEWS_PER_PAGE_INITIAL,
      );

      if (reviews.length === 0) {
        return null;
      }

      const handleOpenModal = () => {
        openReviewsModal(
          type,
          productSku,
          type === "product" ? data : undefined,
        );
      };

      return (
        <div
          id="product-reviews"
          className="bg-surface-3 py-8 text-white flex flex-col gap-8 lg:grid lg:grid-cols-[380px_1fr]"
        >
          <div className="relative flex flex-row gap-4 lg:gap-8 px-6">
            <div className="flex flex-col items-center justify-center w-1/2">
              <div className="text-5xl lg:text-7xl mb-2">
                {averageRating.toFixed(1)}
              </div>
              <p className="text-sm lg:text-md text-center">
                {totalReviews.toLocaleString(locale)}{" "}
                {translate("Trustpilot_Reviews")}
              </p>
              <div className="flex items-center justify-center mt-2">
                <StarRating rating={averageRating} starClassName="h-5 w-5" />
              </div>
            </div>
            <div className="flex items-center justify-center w-1/2">
              <button
                onClick={handleOpenModal}
                className="w-full text-center py-2 border border-white font-medium rounded-md text-md lg:text-base whitespace-nowrap hover:bg-white hover:text-black transition-colors"
              >
                {translate("Trustpilot_View_All_Reviews")}
              </button>
            </div>
          </div>

          <div className="relative flex-1">
            <Swiper
              settings={{
                template: "template1",
                itemsInView: {
                  initial: "1.1",
                  md: "2.5",
                  lg: "2.5",
                  xl: "3.5",
                },
                spacing: {
                  gap: "sm",
                  y: "none",
                },
                navigation: {
                  variants: {
                    color: "surface",
                  },
                },
              }}
            >
              {reviews.map((review) => (
                <ReviewCard
                  key={review.id}
                  review={review}
                  className="h-full"
                  truncateText
                />
              ))}
            </Swiper>
          </div>
        </div>
      );
    },
  ),
);
